<script>
import Modal from './Modal.vue';
import Composer from '@/components/vendasta/Composer.vue';
import SuggestedPostModalContent from '@/components/marketing-calendar/SuggestedPostModalContent.vue';
import partnerPointsApi from '@/services/Lopp/partner-points-service';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';

export default {
	name: 'SocialComposer',
	components: {
		Modal,
		Composer,
		SuggestedPostModalContent,
	},
	props: {
		mediaData: {
			type: Array,
			default: () => [],
		},
		campaignId: {
			type: Number,
		},
		mediaTypeId: {
			type: Number,
		},
		image: {
			type: String,
			default: '',
		},
		content: {
			type: String,
			default: '',
		},
		isBB: {
			type: Boolean,
			default: false,
		},
		isCreatePost: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			showModal: false,
			addModalToDOM: true,
			activeTab: '',
			actionBtnName: 'Next',
			invalidPost: true,
			showControls: true,
			waiting: false,
			cancelBtn: true,
			scheduleDate: new Date(),
			composerReady: false,
		};
	},
	computed: {
		...mapGetters(['partnerPointsProfile']),
		initialTab() {
			return this.isBB ? 'Compose Post' : 'Suggested Content';
		},
		isActionButtonDisabled() {
			return this.invalidPost && this.activeTab === 'Compose Post';
		},
	},
	methods: {
		handleClose() {
    // Reset the state to initial values and emit a close event
    this.resetState();
    this.$emit('socialComposerClose');
    this.toggleModalInDOM();
		},
		resetState() {
			this.activeTab = this.initialTab;
			this.actionBtnName = 'Next';
			this.showControls = true;
			this.showModal = false;
			// Prepare for resetting the Vendasta Composer by removing the modal
			this.addModalToDOM = false;
			this.composerReady = false;
		},
		toggleModalInDOM() {
			// Delay to ensure the modal is properly reset before re-adding to the DOM
			setTimeout(() => {
				this.addModalToDOM = true;
			}, 50);
		},
		// this method is only called from parent components
		validateShowModal() {
			this.waiting = false;
			this.cancelBtn = true;
			this.actionBtnName = 'Next';
			this.showModal = true;
		},
		// this method is only called from parent components
		handleCreatePost() {
			this.activeTab = 'Compose Post';
			this.$refs.composer.displayComposer();
		},
		handleSuggestedContentClick() {
			this.activeTab = 'Suggested Content';
			this.actionBtnName = 'Next';
		},
		handleComposePostClick() {
			this.activeTab = 'Compose Post';
			this.actionBtnName = 'Next';
			this.$refs.composer.displayComposer();
		},
		handlePreviewPostClick() {
			if (!this.invalidPost) {
				this.activeTab = 'Preview and Post';
				this.actionBtnName = 'Post';
				this.$refs.composer.displayPreview();
			}
		},
		handleActionButtonClick() {
			switch (this.activeTab) {
				case 'Suggested Content':
					this.handleComposePostClick();
					break;
				case 'Compose Post':
					this.handlePreviewPostClick();
					break;
				case 'Preview and Post':
					this.handleSubmitPostClick();
					break;
				default:
					this.handleClose();
					break;
			}
		},
		handleSubmitPostClick() {
			this.waiting = true;
			this.showControls = false;
			this.cancelBtn = false;
			this.addPartnerPoints();
		},
		handleModalDateClick(date = this.scheduleDate) {
			let prepDate = moment(date).hour(10); // Set the hour to 10
			if (moment().isSame(prepDate, 'day')) { // Check if prepDate is today
				prepDate = moment().add(1, 'hours'); // If today, set to an hour from now
			}
			this.scheduleDate = prepDate.toDate(); // Convert back to a Date object for Vue
		},
		async addPartnerPoints() {
			const { campaignId, mediaTypeId } = this.extractCampaignAndMediaIds();
			await this.submitPartnerPoints(mediaTypeId, campaignId);
		},
		extractCampaignAndMediaIds() {
			const { campaignType, mediaType } = this.isBB ? this.partnerPointsProfile : {};
			const campaignId = this.isBB ? campaignType : this.campaignId;
			const mediaTypeId = this.isBB ? mediaType : this.mediaTypeId;
			return { campaignId, mediaTypeId }; // Return the extracted IDs
		},
		async submitPartnerPoints(mediaTypeId, campaignId) {
			const exportType = 'social';
			try {
				const partnerPointsApiCall = !mediaTypeId ?
					partnerPointsApi.toolboxCustomPostAddPointsAsync(exportType) :
					partnerPointsApi.toolboxAddPointsAsync(campaignId, mediaTypeId, exportType);
					await partnerPointsApiCall;
			} finally {
				this.$refs.composer.submitPost();
			}
		},
		handleComposerMessage(messageEvent) {
			if (
				messageEvent.origin ===
				'https://united-shore-financial-services-llc.socialsmbs.com'
			) {
				if (messageEvent.data.status === 'ready') {
					this.composerReady = true;
				} else if (messageEvent.data === 'Valid Post') {
					this.invalidPost = false;
				} else if (messageEvent.data === 'Invalid Post') {
					this.invalidPost = true;
				} else if (messageEvent.data.topic === 'submitResults') {
					/*
						Setting this.activeTab to Post Submitted ensures
						that the switch statement in handlActionButtonClick will 
						cause default to fire. 
					*/
					this.activeTab = 'Post Submitted';
					this.waiting = false;
					this.actionBtnName = 'Close';
					this.cancelBtn = false;
				}
			}
		},
		tryAddMediaToComposer() {
			// Ensure the composer is ready and mediaData is not empty
			if (!this.composerReady || isEmpty(this.mediaData)) return;
	
			// If both conditions are met, proceed to remove any existing media and add new media
			this.$refs.composer.removeMedia();
			this.$refs.composer.addMedia(this.mediaData);
		},
	},
	watch: {
		/*
			SocialComposer needs to send image data to vendasta through the Composer.vue

			Two conditions need to be met before we can do that. 
				1. We have image data in mediaData
				2. The vendasta iframe in Composer.vue has loaded and is ready to recieve data
			
			There is no way of knowing which value will be updated first.

			These two watchers (composerReady, and mediaData) are working in unison

			If the image data gets updated in mediaData before the composer sends a ready status,
			Then the composerReady watcher will add image data to composer.

			If the composer sends a ready status before SocialComposer has image data 
			Then the mediaData watcher will add the image data to the composer. 
		*/
		composerReady() {
			this.tryAddMediaToComposer();
		},
		mediaData() {
			this.tryAddMediaToComposer();
		},
	},
	created() {
		this.activeTab = this.initialTab;
		window.addEventListener('message', this.handleComposerMessage);
	},
	destroyed() {
		window.removeEventListener('message', this.handleComposerMessage);
	},
};
</script>

<template>
	<Modal
		:class="
			isBB
				? 'lg social-share-modal disable-padding'
				: 'md suggested-post-modal disable-padding'
		"
		v-if="addModalToDOM"
		v-show="showModal"
		modalTitle="Compose Post"
		:actionBtnName="actionBtnName"
		:waiting="waiting"
		:show="showModal"
		@close="handleClose"
		@clicked="handleActionButtonClick"
		:cancel="cancelBtn"
		:disabled="isActionButtonDisabled"
		:mediaData="mediaData"
	>
		<div slot="modalContent" class="composer-post-wrapper">
			<nav
				aria-label="Social Post Composer"
				class="social-composer-nav"
				v-if="showControls"
				data-testid="sc-nav"
			>
				<button
					v-if="!isBB && !isCreatePost"
					:class="{
						active: 'Suggested Content' === activeTab,
					}"
					@click="handleSuggestedContentClick"
					data-testid="sc-suggested-content"
				>
					1. Suggested Content
				</button>
				<button
					:class="{
						active: 'Compose Post' === activeTab,
					}"
					@click="handleComposePostClick"
					data-testid="sc-compose-post"
				>
					{{ isBB || isCreatePost ? 1 : 2 }}. Compose Post
				</button>
				<button
					:class="{
						active: 'Preview and Post' === activeTab,
						disabled: activeTab !== 'Preview and Post' && invalidPost,
					}"
					@click="handlePreviewPostClick"
					data-testid="sc-preview-and-post"
				>
					{{ isBB || isCreatePost ? 2 : 3 }}. Preview and Post
				</button>
			</nav>
			<transition name="fade" mode="out-in">
				<SuggestedPostModalContent
					:image="image"
					:content="content"
					v-if="!isBB && !isCreatePost"
					v-show="activeTab === 'Suggested Content'"
					data-testid="sc-suggested-post-modal-content"
				/>
			</transition>
			<transition name="fade" mode="out-in">
				<Composer
					ref="composer"
					v-show="activeTab !== 'Suggested Content'"
					composerType="calendar"
					:scheduleDate="scheduleDate"
					:mediaData="mediaData"
					data-testid="sc-composer"
				></Composer>
			</transition>
		</div>
	</Modal>
</template>

<style lang="scss" scoped>
.composer-post-wrapper {
	position: relative;
}

.social-composer-nav {
	border-bottom: $border-width solid $border-color-light;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	@include rem(padding, 0 20px);
	width: 100%;

	button {
		& + * {
			@include rem(margin-left, 28px);
		}

		@include rem(padding, 0 0 19px);
		text-decoration: none;
		color: $text-base-color;
		position: relative;
		transition: $b360-motion-selection;
		border: none;
		background: none;
		font-size: inherit;
		cursor: pointer;

		&:after {
			content: '';
			position: absolute;
			width: 100%;
			@include rem(height, 2px);
			left: 0;
			@include rem(bottom, -1px);
			background: transparent;
			transition: $b360-motion-selection;
		}

		&:hover,
		&:focus,
		&.active {
			color: $brand-blue;

			&:after {
				background: $brand-blue;
			}

			&.disabled:after {
				background: none;
			}
		}

		&:focus {
			outline: none;
		}

		&.disabled {
			color: $color-disabled;
			cursor: not-allowed;
		}
	}
}
</style>
